/**
 * The people-search dialect of the shared attribute filter.
 *
 * The vocabulary, the row state, the arity predicates and the shape validation
 * all live in `@/features/condition-builder`; what stays here is what is genuinely
 * people's — the field policy the shared validator takes as a parameter, and
 * the `{ attribute: {...} }` serializer with its Ember byte-parity trims.
 */

import type { ValidationIssue } from '@customerio/dryad';
import { parseAttributeEnvelope, unwrapSupportableAttributeEnvelope } from '../attribute-envelope';
import {
  isTwoValueFilterOperator,
  isUnaryFilterOperator,
  validateAttributeFilterState,
  type AttributeFilterState,
} from '../attribute-filter-state';
import {
  groupsForFamilies as sharedGroupsForFamilies,
  isSupportedOperator,
  type AttributeFieldPolicy,
  type GroupableFamily,
  type OperatorGroup,
} from '../operator-groups';
import type { AttributeFilter } from './wire-types';

export type { AttributeFilterState } from '../attribute-filter-state';
export type { GroupableFamily, OperatorGroup, OperatorOption } from '../operator-groups';
export { clearsValues, isTwoValueFilterOperator, isUnaryFilterOperator } from '../attribute-filter-state';
export {
  parseAttributeEnvelope,
  unwrapAttributeEnvelope,
  unwrapSupportableAttributeEnvelope,
  type WireAttribute,
} from '../attribute-envelope';

/**
 * The three field rules the shared vocabulary takes as configuration, in
 * people's spelling.
 *
 * The backend serves one_of as a union of per-attribute-value indexes and
 * keeps no per-value index for cio_id (equality on it is answered by a direct
 * internal-id lookup instead), so it rejects one_of on cio_id at query time.
 * created_in_cio_at takes only timestamp operators. The list cap mirrors
 * filters.MaxOneOfValues: larger lists are rejected at create time.
 */
export const PEOPLE_ATTRIBUTE_FIELD_POLICY: AttributeFieldPolicy = {
  oneOfDisallowedFields: new Set<string>(['cio_id', '_created_in_customerio_at']),
  emailField: 'email',
  oneOfMaxValues: 50,
};

/**
 * Derives the grouped operator dropdown from this surface's declared families,
 * under people's field policy.
 */
export function groupsForFamilies(
  declared: readonly GroupableFamily[],
  field: string,
  allowOneOfOperators = false,
): OperatorGroup[] {
  return sharedGroupsForFamilies(declared, field, PEOPLE_ATTRIBUTE_FIELD_POLICY, allowOneOfOperators);
}

/** Shape-only validation for a people attribute row. */
export function validateAttributeFilter(state: AttributeFilterState): Required<ValidationIssue>[] {
  return validateAttributeFilterState(state, PEOPLE_ATTRIBUTE_FIELD_POLICY);
}

// True when the wire node is an attribute filter with an operator the
// caller's surface declares. Deliberately no string requirement on the
// values: the Ember app's guard for this dialect
// (isSupportedAttributeFilter in app/utils/condition-builder/attribute-filter.ts) claims
// numeric values too and its parse coerces them with String(), so declining
// them here would strand on an error node a leaf the Ember app still edits.
// The device_attribute dialect differs on purpose — see
// device-attribute-plugin.ts.
export function isSupportedAttributeFilter(
  json: unknown,
  families: readonly GroupableFamily[],
  allowOneOfOperators = false,
): boolean {
  const unwrapped = unwrapSupportableAttributeEnvelope(json, 'attribute');
  if (unwrapped == null) {
    return false;
  }
  if (unwrapped.attribute.value2 != null && !isTwoValueFilterOperator(unwrapped.operator)) {
    return false;
  }
  return isSupportedOperator(unwrapped.operator, families, allowOneOfOperators);
}

// Parses a supported wire node into filter state. Call only after
// isSupportedAttributeFilter.
export function parseAttributeFilter(json: unknown): AttributeFilterState {
  return parseAttributeEnvelope(json, 'attribute');
}

// Serializes filter state to the wire shape: `{ attribute: {...} }`, with '!'
// operators becoming a `{ not: ... }` wrapper, unary operators dropping their
// value, and between operators carrying value2.
//
// The trims are this dialect's contract, not tidiness: Ember's
// serializeAttributeFilter (app/utils/condition-builder/attribute-filter.ts) trims
// field/value/value2 for the `{ attribute }` leaf, so a claimed leaf holding
// padded (or, via parse's String coercion, numeric) stored values rewrites on
// the next save in both apps identically. The device_attribute leaf's
// serializer never trims — per-dialect in Ember and here alike. Pinned by the
// 'attribute value normalization' fixtures in people-filter-format.test.ts.
export function serializeAttributeFilter(state: AttributeFilterState): AttributeFilter | { not: AttributeFilter } {
  const negated = state.operator.startsWith('!');
  const wireOperator = negated ? state.operator.slice(1) : state.operator;

  const filter: AttributeFilter = {
    attribute: {
      field: state.field.trim(),
      operator: wireOperator,
    },
  };
  if (!isUnaryFilterOperator(state.operator) && state.value != null) {
    filter.attribute.value = state.value.trim();
  }
  if (isTwoValueFilterOperator(state.operator) && state.value2 != null) {
    filter.attribute.value2 = state.value2.trim();
  }

  if (negated) {
    return { not: filter };
  }
  return filter;
}
